import {Component, Inject, OnInit} from '@angular/core';
import {MAT_DIALOG_DATA, MatDialogModule, MatDialogRef} from '@angular/material/dialog';
import {MatInputModule} from '@angular/material/input';
import {MatButtonModule} from '@angular/material/button';
import {ColorSketchModule} from 'ngx-color/sketch';
import {CommonModule} from '@angular/common';
import {FormControl, FormGroup, ReactiveFormsModule, Validators} from '@angular/forms';
import {MatSelectModule} from '@angular/material/select';
import {MapService} from '../api/services/map.service';
import {UserService} from '../api/services/user.service';
import {ViewService} from '../api/services/view.service';
import {CytoscapeService} from '../services/cy-services/cytoscape.service';
import {MatCheckboxModule} from '@angular/material/checkbox';
import {SavedView} from '../api/interfaces/saved-view';
import {MatIconModule} from '@angular/material/icon';
import {MatTooltipModule} from '@angular/material/tooltip';
import {TextInputComponent} from '../shared/forms/text-input/text-input.component';
import {SlideToggleInputComponent} from '../shared/forms/slide-select/slide-toggle-input.component';
import {TextAreaComponent} from '../shared/forms/text-area/text-area-input.component';
import {SettingsService} from '../api/services/setting.service';
import {MODULE_NAMES} from '@maporium-workspace/shared';
import { SidebarState } from '@maporium-workspace/shared';
import {MapStateService} from '../api/services/map-state.service';
import {DashboardComponent} from '../dashboard/dashboard.component';
import {TranslatePipe} from '@ngx-translate/core';

@Component({
  selector: 'maporium-new-map-dialog',
  template: `
    <div class="w-100 d-flex justify-content-between align-items-center dialog-header">
      <h2 class="align-items-center d-flex m-0 p-0"><span class="material-symbols-rounded me-2">{{data? 'edit' : 'add_circle'}}</span>
        {{ data ? ('SCENES_V1.CREATE.TITLE_UPDATE' | translate) : ('SCENES_V1.CREATE.TITLE_CREATE' | translate) }}
      </h2>
      <span class="icon-smap-action-close close-button" mat-dialog-close [matTooltip]="'SHARE_DIALOG.CLOSE_TOOLTIP' | translate"></span>
    </div>
    <div mat-dialog-content>
      <form [formGroup]="myForm">
        <app-text-input [label]="'CREATE_EDIT_SMAP.FIELDS.NAME' | translate" formControlName="name" [autoFocusAndSelect]="true"></app-text-input>
        <app-text-area [label]="'CREATE_EDIT_SMAP.FIELDS.DESCRIPTION' | translate" formControlName="description"></app-text-area>
        <app-slide-toggle-input [label]="'deployed_code'"
                                [tooltip]="'SCENES_V1.CREATE.LABEL_REMEMBER_STATE' | translate"
                                [isIcon]="true"
                                *ngIf="hasStates"
                                [matTooltip]="'SCENES_V1.CREATE.TOOLTIP_REMEMBER_STATE' | translate"
                                formControlName="includeCurrentState"></app-slide-toggle-input>
        <app-slide-toggle-input [label]="'select'"
                                [tooltip]="'SCENES_V1.CREATE.LABEL_REMEMBER_SELECTION' | translate"
                                [isIcon]="true"
                                [matTooltip]="'SCENES_V1.CREATE.TOOLTIP_REMEMBER_SELECTION' | translate"
                                formControlName="includeSelected"></app-slide-toggle-input>
        <app-slide-toggle-input [label]="maporiumIcon"
                                [matTooltip]="'SCENES_V1.CREATE.TOOLTIP_REMEMBER_LINKAGE' | translate"
                                [tooltip]="'SCENES_V1.CREATE.LABEL_REMEMBER_LINKAGE' | translate"
                                [isIcon]="true"
                                [isMaporiumIcon]="true"
                                formControlName="includeLinkState"></app-slide-toggle-input>
        <app-slide-toggle-input [label]="'dock_to_left'"
                                [isIcon]="true"
                                [matTooltip]="'SCENES_V1.CREATE.TOOLTIP_REMEMBER_SIDEBAR' | translate"
                                [tooltip]="'SCENES_V1.CREATE.LABEL_REMEMBER_SIDEBAR' | translate"
                                formControlName="includeSidebar"></app-slide-toggle-input>
        <app-slide-toggle-input [label]="'dock_to_bottom'"
                                [isIcon]="true"
                                [matTooltip]="'SCENES_V1.CREATE.TOOLTIP_SHOW_DESCRIPTION' | translate"
                                [tooltip]="'SCENES_V1.CREATE.LABEL_SHOW_DESCRIPTION' | translate"
                                formControlName="showDescription"></app-slide-toggle-input>
        <p class="mt-5"></p>
        <app-slide-toggle-input [label]="'visibility_off'"
                                [isIcon]="true"
                                [matTooltip]="'SCENES_V1.CREATE.TOOLTIP_HIDE_FROM_VIEWERS' | translate"
                                [tooltip]="'FILTER_DIALOG.HIDE_FROM_VIEWERS' | translate"
                                formControlName="excludeFromReadOnly"></app-slide-toggle-input>
        <app-slide-toggle-input *ngIf="data"
                                [label]="'crop_free'"
                                [isIcon]="true"
                                [matTooltip]="'SCENES_V1.CREATE.TOOLTIP_UPDATE_CANVAS' | translate"
                                [tooltip]="'SCENES_V1.CREATE.LABEL_UPDATE_CANVAS' | translate"
                                formControlName="updateFromCurrentCanvas"></app-slide-toggle-input>
      </form>
    </div>
    <div mat-dialog-actions class="d-flex justify-content-evenly">
      <button class="dialog-action-btn dialog-action-btn-cancel" mat-dialog-close>{{ 'CREATE_MAP_DIALOG.CANCEL' | translate }}</button>
      <button class="dialog-action-btn dialog-action-btn-primary"
              (click)="data ? edit() : create()"
              [disabled]="!myForm.valid"
              cdkFocusInitial>{{ data ? ('CREATE_MAP_DIALOG.UPDATE' | translate) : ('CREATE_MAP_DIALOG.CREATE' | translate) }}</button>
    </div>
  `,
  imports: [
    MatDialogModule,
    MatInputModule,
    MatButtonModule,
    ColorSketchModule,
    CommonModule,
    ReactiveFormsModule,
    MatSelectModule,
    MatCheckboxModule,
    MatIconModule,
    MatTooltipModule,
    TextInputComponent,
    SlideToggleInputComponent,
    TextAreaComponent,
    TranslatePipe
  ],
  standalone: true
})
/**
 * Dialog for new "Scene" ex "Saved View"
 */
export class CreateNewViewDialogComponent implements OnInit{
  myForm: FormGroup;
  MODULE_NAMES = MODULE_NAMES;
  hasStates = false;
  iconsFromEnum = [
    'icon-smap-linkage-all',
    'icon-smap-linkage-none',
    'icon-smap-linkage-attached',
  ]
  constructor(
    @Inject(MAT_DIALOG_DATA) public data: SavedView,
    private matDialogRef: MatDialogRef<CreateNewViewDialogComponent>,
              private userService: UserService,
              private cyService: CytoscapeService,
              private mapService: MapService,
              private stateService: MapStateService,
              private settingsService: SettingsService,
              private viewService: ViewService) {
    this.myForm = new FormGroup({
      name: new FormControl('', [Validators.required]),
      description: new FormControl(''),
      includeSelected: new FormControl(true),
      includeSidebar: new FormControl(true),
      includeLinkState: new FormControl(true),
      includeCurrentFilter: new FormControl(false),
      includeCurrentState: new FormControl(true),
      excludeFromReadOnly: new FormControl(false),
      showDescription: new FormControl(true),
      updateFromCurrentCanvas: new FormControl(false),
    });
  }

  ngOnInit(): void {
    if(this.data) {
      this.myForm.get('includeLinkState')?.setValue(this.data.linkState !== null);
      this.myForm.get('includeSidebar')?.setValue(this.data.sidebarState !== null);
      this.myForm.get('includeCurrentState')?.setValue(this.data.includeCurrentState !== null);
      this.myForm.get('includeSelected')?.setValue(this.data.selected !== null);
      this.myForm.patchValue(this.data);
    }

    setTimeout(() => {
      this.stateService.findAllForMap(this.mapService.getCurrentSelectedMapFromStore()?.id as string).subscribe((states) => {
        this.hasStates = states.length > 0;
      });
    },200)
  }

  create() {
    if(this.myForm.valid) {
      const cy = this.cyService.cy;
      let sidebarWidth = this.settingsService.getLocalStorageSettings().sidebarWidth;
      //In some rare cases the sidebarWidth may not be set
      if(!sidebarWidth || sidebarWidth === 0) {
        sidebarWidth = this.settingsService.getSidebarWidthReal();
      }

      if (!cy) {
        return;
      }
      const state = this.stateService.getCurrentLocalState();
      const payload: SavedView = {
        name: this.myForm.value.name,
        pan: cy.pan(),
        zoom: cy.zoom(),
        // capture the current visible viewport extent of the map
        extent: cy.extent(),
        excludeFromReadOnly: this.myForm.value.excludeFromReadOnly,
        includeCurrentState: this.myForm.value.includeCurrentState,
        showDescription: this.myForm.value.showDescription,
        savedStateId: state?.id,
        description: this.myForm.value.description,
        map: this.mapService.getCurrentSelectedMapFromStore(),
        linkState: this.myForm.value.includeLinkState ? this.settingsService.linkViewMode : null,
        // MAP-1017: the "other links" treatment rides the same "remember link display mode" toggle.
        linkOtherLinksMode: this.myForm.value.includeLinkState ? this.settingsService.otherLinksMode : null,
        otherNodesMode: this.settingsService.otherNodesMode,
        sidebarState: this.myForm.value.includeSidebar ? this.settingsService.getLocalStorageSettings().isSidebarOpen ? SidebarState.FORCE_OPEN : SidebarState.FORCE_CLOSE : null,
        selected: this.myForm.value.includeSelected ? cy.$(':selected').map(node => node.data('id')) : null,
        collapsedNodes: this.cyService.cy?.nodes().filter(node => node.data('collapsed')).map(node => node.data('id')) as string[],
        metadata: {
          sidebarWidth: sidebarWidth as number,
          focusedElementId: DashboardComponent.tappedNode?.id || DashboardComponent.tappedLink?.id || undefined,
          // capture container dimensions for consistent playback across devices
          containerWidth: cy.width(),
          containerHeight: cy.height()
        }
      }
      this.viewService.create(payload).subscribe((res) => this.matDialogRef.close(res));
    }
  }

  edit() {
    if (!this.myForm.valid) {
      return;
    }
    const cy = this.cyService.cy;
    const replaceCanvas = !!this.myForm.value.updateFromCurrentCanvas;
    const state = this.stateService.getCurrentLocalState();

    let sidebarWidth = this.settingsService.getLocalStorageSettings().sidebarWidth;
    if (!sidebarWidth || sidebarWidth === 0) {
      sidebarWidth = this.settingsService.getSidebarWidthReal();
    }

    const includeSelected = this.myForm.value.includeSelected;
    const includeSidebar = this.myForm.value.includeSidebar;
    const includeLinkState = this.myForm.value.includeLinkState;
    const includeCurrentState = this.myForm.value.includeCurrentState;

    const payload: SavedView = {
      ...this.data,
      name: this.myForm.value.name,
      description: this.myForm.value.description,
      excludeFromReadOnly: this.myForm.value.excludeFromReadOnly,
      showDescription: this.myForm.value.showDescription,
      includeCurrentState: includeCurrentState,
      savedStateId: includeCurrentState ? state?.id : undefined,
      linkState: includeLinkState ? this.settingsService.linkViewMode : null,
      // MAP-1017: the "other links" treatment rides the same "remember link display mode" toggle.
      linkOtherLinksMode: includeLinkState ? this.settingsService.otherLinksMode : null,
      otherNodesMode: this.settingsService.otherNodesMode,
      sidebarState: includeSidebar
        ? (this.settingsService.getLocalStorageSettings().isSidebarOpen ? SidebarState.FORCE_OPEN : SidebarState.FORCE_CLOSE)
        : null,
      // Same "update from current canvas" gate as pan/zoom below.
      selected: includeSelected
        ? ((replaceCanvas || this.data.selected == null)
          ? (cy ? cy.$(':selected').map(node => node.data('id')) : [])
          : this.data.selected)
        : null,
      pan: replaceCanvas && cy ? cy.pan() : this.data.pan,
      zoom: replaceCanvas && cy ? cy.zoom() : this.data.zoom,
      extent: replaceCanvas && cy ? cy.extent() : this.data.extent,
      collapsedNodes: replaceCanvas && cy
        ? (cy.nodes().filter(node => node.data('collapsed')).map(node => node.data('id')) as string[])
        : this.data.collapsedNodes,
      metadata: {
        ...(this.data.metadata ?? {} as any),
        sidebarWidth: includeSidebar
          ? (sidebarWidth as number)
          : (this.data.metadata?.sidebarWidth ?? (sidebarWidth as number)),
        // Rides with the selection above.
        focusedElementId: includeSelected
          ? ((replaceCanvas || this.data.selected == null)
            ? (DashboardComponent.tappedNode?.id || DashboardComponent.tappedLink?.id || undefined)
            : this.data.metadata?.focusedElementId)
          : this.data.metadata?.focusedElementId,
        containerWidth: replaceCanvas && cy ? cy.width() : this.data.metadata?.containerWidth,
        containerHeight: replaceCanvas && cy ? cy.height() : this.data.metadata?.containerHeight,
      },
    };

    this.viewService.update(payload).subscribe((res) => this.matDialogRef.close(res));
  }

  get maporiumIcon() {
    return this.iconsFromEnum[this.settingsService.linkViewMode];
  }
}
